import type { Edge, Node } from "@xyflow/react";
import type { WikiBacklink, WikiLinkTarget } from "@/api";

export interface ConnectionNodeData extends Record<string, unknown> {
  title: string;
  spaceUuid: string;
  path: string;
  /** In another space (only `org` pages there can be linked). */
  elsewhere: boolean;
  /** The page being viewed, in the middle. */
  center?: boolean;
}

export type ConnectionNode = Node<ConnectionNodeData, "page">;

/** Every page node has an in and an out handle on each side. */
export const HANDLES = { leftIn: "left-in", leftOut: "left-out", rightIn: "right-in", rightOut: "right-out" } as const;

const COLUMN = 340;
const ROW = 72;

const idOf = (spaceUuid: string, path: string) => `${spaceUuid}|${path}`;
const humanize = (edgeType: string) => edgeType.replaceAll("_", " ");

/** Stack `count` rows around y = 0. */
const rowY = (index: number, count: number) => (index - (count - 1) / 2) * ROW;

/**
 * A page's neighborhood: pages linking to it on the left, pages it links to on the right.
 * A page that does both sits on the right, with edges both ways. Only what the reader can see
 * (the API already filtered links and backlinks).
 */
export function connectionsGraph(
  page: { spaceUuid: string; path: string; title: string },
  links: WikiLinkTarget[],
  backlinks: WikiBacklink[],
): { nodes: ConnectionNode[]; edges: Edge[] } {
  const centerId = idOf(page.spaceUuid, page.path);
  const outgoing = new Map<string, WikiLinkTarget>();
  for (const link of links) {
    const id = idOf(link.spaceUuid, link.path);
    if (id !== centerId && !outgoing.has(id)) outgoing.set(id, link);
  }
  const incoming = new Map<string, WikiBacklink>();
  for (const back of backlinks) {
    const id = idOf(back.spaceUuid, back.path);
    if (id !== centerId && !incoming.has(id)) incoming.set(id, back);
  }
  const leftOnly = [...incoming.entries()].filter(([id]) => !outgoing.has(id));
  const right = [...outgoing.entries()];

  const nodes: ConnectionNode[] = [
    {
      id: centerId,
      type: "page",
      position: { x: 0, y: 0 },
      data: { title: page.title, spaceUuid: page.spaceUuid, path: page.path, elsewhere: false, center: true },
    },
  ];
  leftOnly.forEach(([id, back], i) => {
    nodes.push({
      id,
      type: "page",
      position: { x: -COLUMN, y: rowY(i, leftOnly.length) },
      data: {
        title: back.title,
        spaceUuid: back.spaceUuid,
        path: back.path,
        elsewhere: back.spaceUuid !== page.spaceUuid,
      },
    });
  });
  right.forEach(([id, link], i) => {
    nodes.push({
      id,
      type: "page",
      position: { x: COLUMN, y: rowY(i, right.length) },
      data: { title: link.title, spaceUuid: link.spaceUuid, path: link.path, elsewhere: !link.sameSpace },
    });
  });

  const edges: Edge[] = [];
  for (const [id, back] of incoming) {
    const label = back.edgeTypes.map(humanize).join(", ") || undefined;
    // From the left column into the center's left side; from the right column (pages that
    // also link out) into its right side.
    const fromRight = outgoing.has(id);
    edges.push({
      id: `in:${id}`,
      source: id,
      target: centerId,
      sourceHandle: fromRight ? HANDLES.leftOut : HANDLES.rightOut,
      targetHandle: fromRight ? HANDLES.rightIn : HANDLES.leftIn,
      label,
    });
  }
  for (const [id] of outgoing) {
    edges.push({
      id: `out:${id}`,
      source: centerId,
      target: id,
      sourceHandle: HANDLES.rightOut,
      targetHandle: HANDLES.leftIn,
    });
  }
  return { nodes, edges };
}
